import { useState, useEffect } from "react";
import { useSettings } from "@/contexts/SettingsContext";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ProfileSettingsSection } from "@/components/settings/ProfileSettings";
import { StoreDetailsSettings } from "@/components/settings/StoreDetailsSettings";
import { CheckoutSettings } from "@/components/settings/CheckoutSettings";
import { BookingSettings } from "@/components/settings/BookingSettings";
import { StoreManagersSettings } from "@/components/settings/StoreManagersSettings";
import { AccountDeletionSettings } from "@/components/settings/AccountDeletionSettings";
import { InvoiceSettings } from "@/components/settings/InvoiceSettings";
import { AppearanceSettings } from "@/components/settings/AppearanceSettings";
import { BillingPage } from "@/pages/dashboard/BillingPage";
import { isPaidAccess } from "@/lib/plan";
import { FullPageLoader } from "@/components/ui/full-page-loader";
import { useLocation, useNavigate } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import { DashboardHead } from "@/components/SEO/DashboardHead";
import { PageHeader } from "@/components/dashboard/shared/PageHeader";
import { dashboardStyles } from "@/components/dashboard/shared/dashboard-styles";
import { cn } from "@/lib/utils";
import { useStore } from "@/contexts/StoreContext";

const settingsSections = [
  {
    id: "profile",
    title: "Profile",
    description: "Update your personal information",
    component: ProfileSettingsSection,
  },
  {
    id: "appearance",
    title: "Appearance",
    description: "Light, dark, or match this device",
    component: AppearanceSettings,
  },
  {
    id: "billing",
    title: "Billing",
    description: "Manage your plan and Premium access",
    component: BillingSettingsSection,
  },
  {
    id: "store",
    title: "Details",
    description: "Update your store information and branding",
    component: StoreDetailsSettings,
  },
  {
    id: "checkout",
    title: "Checkout",
    description: "Configure payment and checkout options",
    component: CheckoutSettings,
  },
  {
    id: "booking",
    title: "Bookings",
    description: "Configure booking system for your store",
    component: BookingSettings,
  },
  {
    id: "store-managers",
    title: "Store managers",
    description: "Invite team members and manage permissions",
    component: StoreManagersSettings,
  },
  {
    id: "invoices",
    title: "Invoices",
    description:
      "Configure default invoice template, numbering, tax and payment terms",
    component: InvoiceSettings,
  },
  {
    id: "account",
    title: "Account",
    description: "Delete your account and all owned store data",
    component: AccountDeletionSettings,
  },
];

function BillingSettingsSection() {
  return <BillingPage embedded />;
}

export function SettingsPage() {
  const { isLoading } = useSettings();
  const { user, currentStore } = useAuth();
  const { hasPermission } = useStore();
  const [activeSection, setActiveSection] = useState("profile");
  const location = useLocation();
  const navigate = useNavigate();
  const canEditStore = currentStore?.isOwner === true || hasPermission("settings");

  const visibleSections = settingsSections.filter((section) => {
    if (section.id === "profile" || section.id === "account" || section.id === "appearance") {
      return true;
    }
    if (section.id === "billing") {
      return currentStore?.isOwner === true;
    }
    if (section.id === "store-managers") {
      return currentStore?.isOwner === true && isPaidAccess(user);
    }
    return canEditStore;
  });

  useEffect(() => {
    if (location.hash === "#customization") {
      navigate("/dashboard/sales-link", { replace: true });
      return;
    }
    const hash = location.hash.replace("#", "");
    if (hash && visibleSections.find((s) => s.id === hash)) {
      setActiveSection(hash);
      return;
    }
    if (!visibleSections.find((s) => s.id === activeSection)) {
      setActiveSection(visibleSections[0]?.id || "profile");
    }
  }, [location.hash, user?.plan, currentStore?.isOwner, canEditStore]);

  if (isLoading) {
    return <FullPageLoader text="Loading settings" variant="contained" />;
  }

  const ActiveComponent = visibleSections.find(
    (s) => s.id === activeSection,
  )?.component;
  const activeMeta = visibleSections.find((s) => s.id === activeSection);

  return (
    <div className="space-y-6">
      <DashboardHead pageName="Settings" />
      <PageHeader
        title="Settings"
        subtitle="Manage your account, store details, and preferences."
      />

      <div className="flex w-full flex-col gap-5 xl:flex-row xl:gap-8">
        <nav
          aria-label="Settings sections"
          className="no-scrollbar -mx-4 flex h-fit gap-1.5 overflow-x-auto px-4 xl:sticky xl:top-12 xl:mx-0 xl:w-56 xl:shrink-0 xl:flex-col xl:gap-0.5 xl:overflow-visible xl:px-0"
        >
          {visibleSections.map((section) => {
            const isActive = activeSection === section.id;

            return (
              <Button
                key={section.id}
                variant="ghost"
                aria-current={isActive ? "page" : undefined}
                className={cn(
                  dashboardStyles.navButton,
                  "w-auto shrink-0 border border-border px-4 xl:w-full xl:border-transparent xl:px-3",
                  isActive
                    ? cn(dashboardStyles.navActive, "border-transparent")
                    : dashboardStyles.navIdle,
                )}
                onClick={() => {
                  setActiveSection(section.id);
                  window.location.hash = section.id;
                }}
              >
                {section.title}
                <ChevronRight className="ml-auto hidden h-4 w-4 opacity-40 xl:block" />
              </Button>
            );
          })}
        </nav>

        <div className="w-full min-w-0 flex-1">
          <Card className={cn(dashboardStyles.sectionCard, "p-6 sm:p-8")}>
            <div className="mb-6">
              <h2 className="font-heading text-xl font-bold tracking-tight">
                {activeMeta?.title}
              </h2>
              <p className="mt-1.5 text-sm font-medium text-muted-foreground">
                {activeMeta?.description}
              </p>
            </div>

            {ActiveComponent && <ActiveComponent />}
          </Card>
        </div>
      </div>
    </div>
  );
}
